const HAS_TIMEZONE = /(?:Z|[+-]\d{2}:?\d{2})$/;
const ZONELESS = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?)?$/;

const ET_FORMAT = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/New_York",
  month: "2-digit",
  day: "2-digit",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
});

/**
 * Renders the Eastern-time date parts as `MM/DD/YYYY h:mm AM/PM`.
 * @param {Map<string, string>} parts - Date parts keyed by `Intl.DateTimeFormat` part type.
 * @returns {string} The formatted timestamp.
 */
function formatParts(parts: Map<string, string>): string {
  return `${parts.get("month")}/${parts.get("day")}/${parts.get("year")} ${parts.get("hour")}:${parts.get("minute")} ${parts.get("dayPeriod")}`;
}

/**
 * Formats a docs/messages timestamp as `MM/DD/YYYY h:mm AM/PM` in Eastern time, matching the
 * other Workbench panels. Values carrying a zone are converted to ET; zone-less values are
 * Eastern wall-clock already (as recorded by caps2/AppXtender) and are rendered as-is rather
 * than being shifted into the viewer's local zone.
 * @param {string} value - The raw timestamp string.
 * @returns {string} The formatted timestamp, "" when absent, or the raw value when unparseable.
 */
export function formatReceived(value: string): string {
  if (!value) {
    return "";
  }
  if (HAS_TIMEZONE.test(value)) {
    const parsed = new Date(value.replace(" ", "T"));
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }
    return formatParts(new Map(ET_FORMAT.formatToParts(parsed).map((part) => [part.type, part.value])));
  }
  const match = ZONELESS.exec(value);
  if (!match) {
    return value;
  }
  const [, year, month, day, hour24 = "00", minute = "00"] = match;
  const hours = Number(hour24);
  const period = hours >= 12 ? "PM" : "AM";
  return `${month}/${day}/${year} ${hours % 12 || 12}:${minute} ${period}`;
}
